import { Form, Field } from "react-final-form";
import { FORM_ERROR } from "final-form";
import * as FormUtils from "common/FormUtils";
import * as utils from "common/utils";
import Modal from "components/Modal/Modal";
import ModalBody from "components/Modal/ModalBody";
import ModalFooter from "components/Modal/ModalFooter";
import ModalFooterLeft from "components/Modal/ModalFooterLeft";
import ModalFooterRight from "components/Modal/ModalFooterRight";
import Button from "components/Button/Button";
import Alert from "components/Alert";
import { Panel, PanelBody } from "components/Panel";
import { Grid, GridCell } from "components/Grid";
import TextareaField from "components/FormFields/TextareaField";

const MAX_COMMENT_LENGTH = 1000;

/**
 * Removing a paid-for subscription is a support action that has to be explained and, by default,
 * paid back. Hence its own modal rather than the generic confirm: the comment is mandatory and the
 * refund is a decision the admin has to see themselves make.
 *
 * The farm is spelled out in full before the form rather than named in a sentence. The action is
 * destructive and fired from an icon in a dense list, so the first thing to establish is that the
 * right row was clicked.
 */
export default function RemoveLastSubscriptionModal({ farm, removeLastSubscription, close }) {
    const isOnlySubscription = (farm.subscriptionCount || 0) <= 1;

    const validate = (viewModel) => ({
        comment: FormUtils.validators.required(viewModel.comment, "Please say why this subscription is being removed") || FormUtils.validators.maxLength(MAX_COMMENT_LENGTH)(viewModel.comment),
    });

    const submit = async (viewModel) => {
        try {
            await removeLastSubscription({ comment: viewModel.comment });
            close();
        } catch (error) {
            return { [FORM_ERROR]: error.message };
        }
    };

    return (
        <Form validate={validate} onSubmit={submit}>
            {({ handleSubmit, submitting, submitError, dirtySinceLastSubmit }) => {
                const error = !dirtySinceLastSubmit && submitError;
                return (
                    <form onSubmit={handleSubmit}>
                        <Modal title="Remove last subscription" close={close} submitting={submitting} skinny>
                            <ModalBody error={error}>
                                <Panel title="Subscription being removed" skyBlue className="u-mt-lg" notCollapsible>
                                    <PanelBody>
                                        <Grid>
                                            <GridCell>
                                                <ul className="disc">
                                                    <li>The most recent subscription is removed from this farm.</li>
                                                    <li>{isOnlySubscription ? "It is the farm's only subscription, so the farm becomes expired." : "The farm's expiry rolls back to the previous subscription."}</li>
                                                    <li>The payment that bought it is reversed: a refund for the negative of what was charged, against the organisation that paid.</li>
                                                    <li>The two net to zero, so the purchase cancels out of totals rather than disappearing from them.</li>
                                                    <li>The removal is recorded on the farm's audit trail.</li>
                                                </ul>
                                                <div className="Table u-mt-md">
                                                    <table>
                                                        <thead>
                                                            <tr>
                                                                <th>Farm</th>
                                                                <th>Address</th>
                                                                <th>Region</th>
                                                                <th className="u-textCenter" data-width="15">
                                                                    Subs
                                                                </th>
                                                                <th data-width="20">Expires now</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr>
                                                                <td className="u-textBold">{farm.name}</td>
                                                                <td>{farm.address || "-"}</td>
                                                                <td>{farm.region || "-"}</td>
                                                                <td className="u-textCenter">{farm.subscriptionCount}</td>
                                                                <td className={farm.expired ? "u-textError" : ""}>{farm.expired ? "Expired" : utils.dateLong(farm.expirationDate, "-")}</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                                <Alert type="warning" text="This cannot be undone." className="u-mt-md u-mb-0" />
                                            </GridCell>
                                        </Grid>
                                    </PanelBody>
                                </Panel>
                                <Panel title="Reason" green notCollapsible>
                                    <PanelBody>
                                        <Grid>
                                            <GridCell>
                                                <Field name="comment" label="Why is this being removed?" placeholder="Recorded on the farm's audit trail" rows={3} maxLength={MAX_COMMENT_LENGTH} required autoFocus component={TextareaField} />
                                            </GridCell>
                                        </Grid>
                                    </PanelBody>
                                </Panel>
                            </ModalBody>
                            <ModalFooter>
                                <ModalFooterLeft>
                                    <Button id="cancel" onClick={close} secondary disabled={submitting}>
                                        Cancel
                                    </Button>
                                </ModalFooterLeft>
                                <ModalFooterRight>
                                    <Button id="submit" submit primary disabled={submitting} waiting={submitting}>
                                        Remove subscription
                                    </Button>
                                </ModalFooterRight>
                            </ModalFooter>
                        </Modal>
                    </form>
                );
            }}
        </Form>
    );
}
